<template>
  <button
    type="button"
    class="border border-gray-300 rounded-md px-3 py-1 shadow-sm transition duration-300 flex items-start leading-normal text-gray-900 group/ticket focus-visible:border-gray-400"
    :class="[
      selectedTicketType && !props.isSingleSummary ? 'ring-2 ring-primary-500 border-transparent' : '',
      isDisabled ? 'bg-gray-100' : '',
      isDisabled || selectedTicketType ? 'cursor-auto' : 'cursor-pointer hover:bg-gray-50',
    ]"
    :disabled="isDisabled || !isMounted || undefined"
    data-cy="ticket"
    :data-ticket-id="props.ticketingType.id"
    @keydown.left="decrementQuantity"
    @keydown.right="incrementQuantity"
    @keydown.enter.prevent="handleClick"
    @click="handleClick"
  >
    <div
      v-if="!isBulkPurchaseEnabled || props.isSingleSummary"
      class="mr-2.5 w-4 h-9 flex items-center"
    >
      <Icon
        name="custom/check-fill"
        class="size-4 text-primary-500 transition-all duration-300"
        :class="[
          selectedTicketType ? 'opacity-100 scale-100' : 'opacity-0 scale-75',
          props.isSingleSummary && canChangeTicketType ? 'group-hover/ticket:opacity-0 hover-none:opacity-0' : '',
        ]"
      />
      <Icon
        v-if="props.isSingleSummary && canChangeTicketType"
        name="arrow-left"
        class="size-4 text-gray-700 transition-all duration-300 absolute opacity-0 group-hover/ticket:opacity-100 hover-none:opacity-100 cursor-pointer"
        @click="() => {
          emit('edit', props.selectedTicketTypes);
        }"
      />
    </div>

    <div class="flex-1 my-1">
      <div :class="isBulkPurchaseEnabled && !props.isSingleSummary ? 'flex items-start justify-between' : ''">
        <div
          class="flex items-start"
          :class="isBulkPurchaseEnabled && !props.isSingleSummary ? 'flex-col' : 'flex-row'"
        >
          <div class="flex-1 flex items-center leading-normal gap-2 my-1">
            <strong>
              {{ props.ticketingType?.[props.lang]?.name }}
            </strong>

            <div
              v-if="props.ticketingType.availableCapacity === 0 && !props.isSingleSummary"
              class="text-gray-500 shrink-0"
            >
              {{ props.section.$t('poly.guest.capacityLimitReached') }}
            </div>
          </div>
          <div
            class="font-bold text-gray-800"
            data-cy="ticket-price"
            :class="isBulkPurchaseEnabled && !props.isSingleSummary ? 'mb-0.5' : 'my-1'"
          >
            <template v-if="props.ticketingType.isFree">
              {{ props.section.$t('poly.ticket.free') }}
            </template>
            <template v-else>
              {{ useFormatPrice(props.ticketingType.price, props.event?.general?.ticketing?.currency) }}
            </template>
          </div>
        </div>

        <PageFormBasicTicketsTicketQuantity
          v-if="isBulkPurchaseEnabled && !props.isSingleSummary"
          :quantity="quantity"
          :decrement-disabled="quantity === 0 || isDisabled"
          :increment-disabled="quantity >= maximumQuantity || isDisabled"
          @decrement="decrementQuantity"
          @increment="incrementQuantity"
        />
      </div>

      <div
        v-if="props.ticketingType?.[props.lang]?.description"
        class="text-gray-500 text-sm mb-1"
        :class="isBulkPurchaseEnabled && !props.isSingleSummary ? 'mt-1' : ''"
      >
        {{ props.ticketingType?.[props.lang]?.description }}
      </div>
    </div>
  </button>
</template>

<script setup>
import constants from '~/api/shared/_constants.js';

const props = defineProps({
  selectedTicketTypes: {
    type: Array,
    default: () => [],
  },
  ticketingType: {
    type: Object,
    default: null,
  },
  ticketingTypes: {
    type: Array,
    default: () => [],
  },
  lang: {
    type: String,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  guest: {
    type: Object,
    default: null,
  },
  event: {
    type: Object,
    required: true,
  },
  section: {
    type: Object,
    required: true,
  },
  isSingleSummary: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'update:selectedTicketTypes']);
const isMounted = useMounted();

// Computed
const selectedTicketTypes = useVModel(props, 'selectedTicketTypes', emit);
const canChangeTicketType = computed(() => (props.ticketingTypes.length > 1 || props.event?.general?.ticketing?.isBulkPurchaseEnabled) && (props.form?.status?.confirmation || 0) === 0 && !props.form?.extras?.payment?.isPaid && !props.guest?.properties?.ticketType);
const selectedTicketType = computed(() => _find(selectedTicketTypes.value, { id: props.ticketingType.id }));

const isBulkPurchaseEnabled = computed(() => props.event?.general?.ticketing?.isBulkPurchaseEnabled);
const quantity = computed(() => selectedTicketType.value?.quantity || 0);
const maximumQuantity = computed(() => Math.min(props.ticketingType?.availableCapacity || constants.MAXIMUM_QUANTITY_PER_TICKET_TYPE, constants.MAXIMUM_QUANTITY_PER_TICKET_TYPE));

const isDisabled = computed(() => {
  if (props.form?.extras?.payment?.isPaid) {
    return true;
  }
  if (props.ticketingType?.availableCapacity === 0) {
    return true;
  }

  return false;
});

// Methods
const incrementQuantity = () => {
  if (isBulkPurchaseEnabled.value && !isDisabled.value) {
    if (selectedTicketType.value) {
      const newQuantity = Math.min(quantity.value + 1, maximumQuantity.value);

      selectedTicketTypes.value = selectedTicketTypes.value
        .map((ticket) => ticket.id === props.ticketingType.id ? { ...ticket, quantity: newQuantity } : ticket);
    } else {
      selectedTicketTypes.value = [...selectedTicketTypes.value, { id: props.ticketingType.id, quantity: 1 }];
    }
  }
};

const decrementQuantity = () => {
  if (isBulkPurchaseEnabled.value && !isDisabled.value) {
    if (selectedTicketType.value) {
      selectedTicketTypes.value = selectedTicketTypes.value
        .map((ticket) => ticket.id === props.ticketingType.id ? { ...ticket, quantity: Math.max(0, ticket.quantity - 1) } : ticket)
        .filter((ticket) => ticket.quantity > 0);
    }
  }
};

const handleClick = () => {
  if (isDisabled.value) {
    return;
  }

  if (isBulkPurchaseEnabled.value) {
    if (quantity.value === 0) {
      incrementQuantity();
    }
  } else {
    selectedTicketTypes.value = [{
      id: props.ticketingType.id,
      quantity: 1,
    }];
  }
};
</script>
